import React, { useState, useMemo } from 'react'
import {
  Plus, RefreshCw, Loader2, AlertCircle, ChevronRight, ChevronDown,
  Package, Calculator, Send, Trash2, Edit, Settings, CheckCircle2, X
} from 'lucide-react'
import { Card } from '../../../../../../components/ui/Card'
import { Badge } from '../../../../../../components/ui/Badge'

// Status configuration shared across the component
const STATUS_CONFIG = {
  'ACTIVE': { label: 'Activo', color: 'bg-green-600', textColor: 'text-green-100' },
  'DRAFT': { label: 'Borrador', color: 'bg-gray-600', textColor: 'text-gray-100' },
  'INPROGRESS': { label: 'En Progreso', color: 'bg-blue-600', textColor: 'text-blue-100' },
  'FINALIZED': { label: 'Finalizado', color: 'bg-yellow-600', textColor: 'text-yellow-100' },
  'CALCULATED': { label: 'Calculado', color: 'bg-purple-600', textColor: 'text-purple-100' },
  'SENT_TO_SAP': { label: 'Enviado a SAP', color: 'bg-indigo-600', textColor: 'text-indigo-100' },
  'PARTIALLY_SENT': { label: 'Parcial', color: 'bg-orange-600', textColor: 'text-orange-100' },
  'ADJUSTED': { label: 'Ajustado', color: 'bg-cyan-600', textColor: 'text-cyan-100' }
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime()) || date.getFullYear() < 1900) return '-'
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch { return '-' }
}

function formatTime(dateStr) {
  if (!dateStr) return '-'
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime()) || date.getFullYear() < 1900) return '-'
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch { return '-' }
}

/**
 * Inline declaration form - shown as a collapsible section within the consumption card
 */
function DeclarationForm({
  recipe,
  declarationForm,
  setDeclarationForm,
  dosifiersFromAPI,
  hoppersFromAPI,
  loadingDosifiersAPI,
  addingConsumption,
  targetPeriod,
  onDeclare,
  onReloadDosifiers,
  onClose
}) {
  if (!recipe?.Components?.length) return null

  const targetPeriodNumber = targetPeriod
    ? (targetPeriod.PeriodNumber ?? targetPeriod.periodNumber ?? targetPeriod.ID ?? targetPeriod.id)
    : null
  const targetStatus = targetPeriod ? (targetPeriod.Status || targetPeriod.status || '') : ''
  const targetConfig = STATUS_CONFIG[targetStatus] || { label: targetStatus, color: 'bg-gray-600', textColor: 'text-gray-100' }

  return (
    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-semibold text-white">Nueva Declaración de Consumo</h4>
          {targetPeriod ? (
            <span className="flex items-center gap-1.5 text-xs text-gray-300">
              <span className="text-gray-500">→ Periodo</span>
              <span className="font-mono text-white">#{targetPeriodNumber}</span>
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${targetConfig.color} ${targetConfig.textColor}`}>
                {targetConfig.label}
              </span>
            </span>
          ) : (
            <span className="text-xs text-yellow-400 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              Selecciona un periodo en la tabla
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {loadingDosifiersAPI ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin mr-2" />
          <p className="text-gray-400 text-sm">Cargando dosificadores...</p>
        </div>
      ) : dosifiersFromAPI.length === 0 ? (
        <div className="text-center py-4">
          <AlertCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No hay dosificadores disponibles</p>
          <button
            onClick={onReloadDosifiers}
            className="mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-xs"
          >
            <RefreshCw className="w-3 h-3 inline mr-1" />
            Recargar
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          {/* Component selector */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Componente <span className="text-red-400">*</span>
            </label>
            <select
              value={declarationForm.componentSapCode}
              onChange={(e) => setDeclarationForm(prev => ({ ...prev, componentSapCode: e.target.value, hopperId: '' }))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">Selecciona componente</option>
              {recipe.Components.map((comp, idx) => (
                <option key={idx} value={comp.SapCode}>
                  {comp.SapCode} - {comp.Description || 'Sin desc.'}
                </option>
              ))}
            </select>
          </div>

          {/* Dosifier selector */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Dosificador <span className="text-red-400">*</span>
            </label>
            <select
              value={declarationForm.dosifierId}
              onChange={(e) => setDeclarationForm(prev => ({ ...prev, dosifierId: e.target.value, hopperId: '' }))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">Selecciona dosificador</option>
              {dosifiersFromAPI.map((d) => (
                <option key={d.id} value={d.id}>{d.code || d.name}</option>
              ))}
            </select>
          </div>

          {/* Hopper selector */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Hopper <span className="text-red-400">*</span>
            </label>
            <select
              value={declarationForm.hopperId}
              onChange={(e) => setDeclarationForm(prev => ({ ...prev, hopperId: e.target.value }))}
              disabled={!declarationForm.dosifierId}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{declarationForm.dosifierId ? 'Selecciona hopper' : 'Primero dosificador'}</option>
              {hoppersFromAPI
                .filter(h => String(h.dosifierId) === String(declarationForm.dosifierId))
                .map((h) => (
                  <option key={h.id} value={h.id}>{h.code || h.name}</option>
                ))}
            </select>
          </div>

          {/* Declare button */}
          <div>
            <button
              onClick={onDeclare}
              disabled={addingConsumption || !targetPeriod || !declarationForm.componentSapCode || !declarationForm.dosifierId || !declarationForm.hopperId}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm font-medium"
              title={!targetPeriod ? "Selecciona un periodo en la tabla primero" : `Declarar consumo en periodo #${targetPeriodNumber}`}
            >
              {addingConsumption ? (
                <><Loader2 className="w-4 h-4 animate-spin" /><span>Declarando...</span></>
              ) : (
                <><Plus className="w-4 h-4" /><span>Declarar</span></>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Expandable row content: shows consumption declarations for a period
 */
function PeriodConsumptionsTable({
  period,
  periodID,
  periodConsumptions,
  isLoading,
  canEdit,
  onEditConsumption,
  onDeleteConsumption,
  onAdjustConsumption,
  onCalculate,
  onSendToSAP,
  onDeletePeriod,
  loadingConsumptions,
  sendingToSAP,
  readOnly = false,
}) {
  const periodStatus = period.Status || period.status || ''
  const periodEnd = period.PeriodEnd || period.periodEnd || period.period_end
  const hasValidPeriodEnd = periodEnd && (() => {
    try {
      const d = new Date(periodEnd)
      return !isNaN(d.getTime()) && d.getTime() > 0
    } catch {
      return false
    }
  })()
  const canCalculate = periodStatus !== 'SENT_TO_SAP' && hasValidPeriodEnd

  return (
    <div className="p-4 bg-gray-900/50">
      {/* Action buttons */}
      {!readOnly && (
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-300">
          Declaraciones del Periodo {periodID}
        </h4>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onCalculate}
            disabled={!canCalculate || loadingConsumptions}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-xs"
            title={periodStatus === 'SENT_TO_SAP' ? "No se puede calcular: enviado a SAP" : !hasValidPeriodEnd ? "El período debe tener fecha de fin para calcular" : "Calcular consumos"}
          >
            {loadingConsumptions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calculator className="w-3 h-3" />}
            <span>Calcular</span>
          </button>
          <button
            onClick={onSendToSAP}
            disabled={periodStatus === 'SENT_TO_SAP' || sendingToSAP || loadingConsumptions}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-xs"
            title={periodStatus === 'SENT_TO_SAP' ? "Ya enviado a SAP" : "Enviar a SAP"}
          >
            {sendingToSAP ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            <span>Enviar a SAP</span>
          </button>
          {typeof onDeletePeriod === 'function' && (
            <button
              onClick={onDeletePeriod}
              disabled={periodStatus === 'SENT_TO_SAP'}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-xs"
              title={periodStatus === 'SENT_TO_SAP' ? "No se puede eliminar: enviado a SAP" : "Eliminar periodo"}
            >
              <Trash2 className="w-3 h-3" />
              <span>Eliminar</span>
            </button>
          )}
        </div>
      </div>
      )}
      {readOnly && (
        <h4 className="text-sm font-semibold text-gray-300 mb-3">
          Declaraciones del Periodo {periodID}
        </h4>
      )}

      {/* Consumptions content */}
      {isLoading ? (
        <div className="text-center py-6 text-gray-500">
          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin opacity-50" />
          <p className="text-sm">Cargando declaraciones...</p>
        </div>
      ) : periodConsumptions.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No hay declaraciones en este periodo</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400">Componente</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400">Dosificador</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400">Tolva</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-400">Calculada</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-400">Ajustada</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-gray-400">Enviada SAP</th>
                {!readOnly && (
                <th className="text-center py-2 px-3 text-xs font-semibold text-gray-400">Acciones</th>
                )}
              </tr>
            </thead>
            <tbody>
              {periodConsumptions
                .filter(c => (c.ComponentSapCode || c.componentSapCode || '').trim() !== '')
                .map((consumption, idx) => {
                  const consumptionID = consumption.ID || consumption.id || idx
                  const componentCode = consumption.ComponentSapCode || consumption.componentSapCode || '-'
                  const dosingUnit = consumption.DosingUnit || consumption.dosingUnit || '-'
                  const hopper = consumption.DosingHopper || consumption.hopper || consumption.Hopper || '-'
                  const calculatedQty = consumption.CalculatedQuantity || consumption.calculatedQuantity || 0
                  const adjustedQty = consumption.AdjustedQuantity || consumption.adjustedQuantity || 0
                  const sentQty = consumption.SentToSAPQuantity || consumption.sentToSAPQuantity || 0

                  return (
                    <tr key={consumptionID} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-2 px-3"><span className="text-white font-mono text-xs">{componentCode}</span></td>
                      <td className="py-2 px-3"><span className="text-gray-300 text-xs">{dosingUnit}</span></td>
                      <td className="py-2 px-3"><span className="text-gray-300 text-xs">{hopper}</span></td>
                      <td className="py-2 px-3 text-right"><span className="text-blue-400 text-xs font-mono">{calculatedQty.toFixed(3)}</span></td>
                      <td className="py-2 px-3 text-right"><span className="text-purple-400 text-xs font-mono">{adjustedQty.toFixed(3)}</span></td>
                      <td className="py-2 px-3 text-right"><span className="text-green-400 text-xs font-mono">{sentQty.toFixed(3)}</span></td>
                      {!readOnly && (
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); onEditConsumption(consumption) }}
                            disabled={!canEdit}
                            className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Editar"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); onDeleteConsumption(consumption) }}
                            disabled={!canEdit}
                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); onAdjustConsumption(consumption) }}
                            disabled={!canEdit}
                            className="p-1.5 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Ajustar cantidad"
                          >
                            <Settings className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      )}
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * Unified Consumption Periods Manager.
 * Combines: period list, declaration form, and expandable consumption rows.
 */
function ConsumptionPeriodsManager({
  // Period state
  periods,
  activePeriod,
  loadingPeriods,
  expandedPeriodId,
  expandedPeriodConsumptions,
  loadingExpandedPeriod,
  loadingConsumptions,
  sendingToSAP,
  // Order/line context
  selectedOrder,
  selectedLine,
  // Declaration form state
  recipe,
  declarationForm,
  setDeclarationForm,
  dosifiersFromAPI,
  hoppersFromAPI,
  loadingDosifiersAPI,
  addingConsumption,
  // Handlers
  onCreateNewPeriod,
  onLoadPeriods,
  onSetActivePeriod,
  onExpandPeriod,
  onCollapsePeriod,
  onLoadPeriodConsumptions,
  onCalculateConsumptions,
  onSendToSAP,
  onDeletePeriod,
  onFinalizePeriodAndNext,
  onDeclareConsumption,
  onReloadDosifiers,
  onEditConsumption,
  onDeleteConsumption,
  onAdjustConsumption,
  onEditPeriodStart,
  onEditPeriodEnd,
  onLoadConsumptions,
  // Periodo destino para declaraciones
  selectedPeriodId,
  onSelectedPeriodChange,
  // Helpers
  getFactoryFromLine,
  sapConsumptionService,
  readOnly = false,
}) {
  const [showDeclarationForm, setShowDeclarationForm] = useState(false)

  const targetPeriod = selectedPeriodId
    ? periods.find(p => (p.ID || p.id) === selectedPeriodId) ?? null
    : null

  return (
    <Card className="mb-6">
      <div className="p-4">
        {/* Title bar with actions */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            Periodos de Consumo
            {periods.length > 0 && (
              <Badge variant="secondary" className="text-xs">{periods.length}</Badge>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {!readOnly && recipe?.Components?.length > 0 && (
              <button
                onClick={() => setShowDeclarationForm(!showDeclarationForm)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                  showDeclarationForm
                    ? 'bg-purple-700 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                <Plus className="w-4 h-4" />
                <span>Declarar Consumo</span>
              </button>
            )}
            {!readOnly && typeof onCreateNewPeriod === 'function' && (
              <button
                onClick={onCreateNewPeriod}
                disabled={loadingPeriods || !selectedOrder || !selectedLine}
                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm"
                title="Crear nuevo periodo"
              >
                {loadingPeriods ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                <span>Nuevo Periodo</span>
              </button>
            )}
            <button
              onClick={onLoadPeriods}
              disabled={loadingPeriods}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm"
            >
              {loadingPeriods ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Inline declaration form (collapsible) */}
        {!readOnly && showDeclarationForm && (
          <DeclarationForm
            recipe={recipe}
            declarationForm={declarationForm}
            setDeclarationForm={setDeclarationForm}
            dosifiersFromAPI={dosifiersFromAPI}
            hoppersFromAPI={hoppersFromAPI}
            loadingDosifiersAPI={loadingDosifiersAPI}
            addingConsumption={addingConsumption}
            targetPeriod={targetPeriod}
            onDeclare={onDeclareConsumption}
            onReloadDosifiers={onReloadDosifiers}
            onClose={() => setShowDeclarationForm(false)}
          />
        )}

        {/* Active period info bar */}
        {activePeriod && (
          <div className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-2 mb-4 border border-gray-700">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">Periodo activo:</span>
                <span className="text-white font-mono">
                  #
                  {activePeriod.PeriodNumber ??
                    activePeriod.periodNumber ??
                    activePeriod.ID ??
                    activePeriod.id}
                </span>
              </div>
              {(() => {
                const status = activePeriod.Status || activePeriod.status || 'UNKNOWN'
                const config = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-600', textColor: 'text-gray-100' }
                return <Badge className={`${config.color} ${config.textColor} text-xs`}>{config.label}</Badge>
              })()}
              <span className="text-gray-400">
                {activePeriod.PeriodStart || activePeriod.periodStart
                  ? formatDate(activePeriod.PeriodStart || activePeriod.periodStart) + ' ' + formatTime(activePeriod.PeriodStart || activePeriod.periodStart)
                  : 'N/A'}
                {' - '}
                {activePeriod.PeriodEnd || activePeriod.periodEnd
                  ? formatDate(activePeriod.PeriodEnd || activePeriod.periodEnd) + ' ' + formatTime(activePeriod.PeriodEnd || activePeriod.periodEnd)
                  : 'En curso'}
              </span>
            </div>
            {(() => {
              const status = activePeriod.Status || activePeriod.status || ''
              return !readOnly && (status === 'ACTIVE' || status === 'DRAFT' || status === 'INPROGRESS') ? (
                <button
                  onClick={onFinalizePeriodAndNext}
                  disabled={loadingPeriods}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-xs"
                >
                  {loadingPeriods ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  <span>Finalizar y Crear Siguiente</span>
                </button>
              ) : null
            })()}
          </div>
        )}

        {/* Periods content */}
        {loadingPeriods ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin mr-2" />
            <p className="text-gray-400 text-sm">Cargando periodos...</p>
          </div>
        ) : periods.length === 0 ? (
          <div className="text-center py-6">
            <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-gray-300 text-sm font-medium mb-1">No hay periodos disponibles</p>
            <div className="text-xs text-gray-500 space-y-1 mt-2">
              <p><strong>Regla 1:</strong> El periodo 1 se crea al iniciar la orden (boton "Inicio OF") con la fecha/hora seleccionada</p>
              <p>Si la orden ya esta iniciada, haz clic en "Actualizar" para recargar</p>
            </div>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-900/50 border-b border-gray-700">
                    {!readOnly && (
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase w-12" title="Selecciona el periodo destino para declarar consumos">Decl.</th>
                    )}
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase w-16">Nº</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase">Receta</th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase w-20">Decl.</th>
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase w-28">Estado</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase">Inicio</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase">Fin</th>
                    {!readOnly && (
                    <th className="text-center py-2.5 px-3 text-xs font-semibold text-gray-400 uppercase w-24">Acciones</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {periods.map((period) => {
                    const periodID = period.ID || period.id
                    const periodNumber =
                      period.PeriodNumber ??
                      period.periodNumber ??
                      period.PeriodNo ??
                      period.periodNo ??
                      periodID
                    const recipeCode = period.MrRecipeSapCode || period.mr_recipe_sap_code || '-'
                    const status = period.Status || period.status || 'UNKNOWN'
                    const isPeriodActive = period.IsActive === true || period.isActive === true || period.is_active === true
                    const isExpanded = expandedPeriodId === periodID
                    const canEditPeriod = status !== 'SENT_TO_SAP'
                    const isSelectedForDeclaration = selectedPeriodId === periodID
                    const canSelectForDeclaration = status !== 'SENT_TO_SAP'

                    // Count declarations
                    const consumptionsList = period.Consumptions || period.consumptions || []
                    const declarationsCount = consumptionsList.filter(c => (c.ComponentSapCode || c.componentSapCode || '').trim() !== '').length

                    const config = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-600', textColor: 'text-gray-100' }
                    const startDate = period.PeriodStart || period.periodStart
                    const endDate = period.PeriodEnd || period.periodEnd

                    return (
                      <React.Fragment key={periodID}>
                        <tr
                          className={`border-b border-gray-800 hover:bg-gray-700/50 transition-colors cursor-pointer ${isSelectedForDeclaration ? 'bg-purple-900/20 ring-1 ring-inset ring-purple-700/50' : isPeriodActive ? 'bg-blue-900/20' : ''}`}
                          onClick={async (e) => {
                            if (e.target.closest('button') || e.target.closest('input[type="checkbox"]')) return
                            if (isExpanded) {
                              onCollapsePeriod(periodID)
                            } else {
                              onExpandPeriod(periodID)
                              await onLoadPeriodConsumptions(periodID)
                            }
                          }}
                        >
                          {!readOnly && (
                          <td className="py-2.5 px-3 text-center">
                            <input
                              type="checkbox"
                              checked={isSelectedForDeclaration}
                              disabled={!canSelectForDeclaration}
                              className={`w-4 h-4 rounded border-gray-600 bg-gray-800 transition-colors
                                ${canSelectForDeclaration
                                  ? 'cursor-pointer accent-purple-500 hover:border-purple-400'
                                  : 'opacity-40 cursor-not-allowed'}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                if (!canSelectForDeclaration) return
                                onSelectedPeriodChange?.(isSelectedForDeclaration ? null : periodID)
                              }}
                              title={
                                !canSelectForDeclaration
                                  ? 'No se puede seleccionar: periodo enviado a SAP'
                                  : isSelectedForDeclaration
                                    ? 'Deseleccionar periodo'
                                    : 'Seleccionar como destino de declaraciones'
                              }
                            />
                          </td>
                          )}
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-1.5">
                              <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                              <span className="text-white font-mono text-sm">{periodNumber}</span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="text-gray-300 font-mono text-sm">{recipeCode}</span>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <Badge variant="secondary" className="text-xs">{declarationsCount}</Badge>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <Badge className={`${config.color} ${config.textColor} text-xs`}>{config.label}</Badge>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="text-gray-300 text-xs">{formatDate(startDate)} <span className="text-gray-500">{formatTime(startDate)}</span></span>
                          </td>
                          <td className="py-2.5 px-3">
                            {endDate ? (
                              <span className="text-gray-300 text-xs">{formatDate(endDate)} <span className="text-gray-500">{formatTime(endDate)}</span></span>
                            ) : (
                              <span className="text-yellow-400 italic text-xs">En curso</span>
                            )}
                          </td>
                          {!readOnly && (
                          <td className="py-2.5 px-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onEditPeriodEnd?.(period)
                                }}
                                disabled={!canEditPeriod}
                                className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={canEditPeriod ? 'Editar fin del período' : 'No editable: período enviado a SAP'}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                          )}
                        </tr>

                        {/* Expandable consumption rows */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={readOnly ? 6 : 8} className="p-0">
                              <PeriodConsumptionsTable
                                period={period}
                                periodID={periodID}
                                periodConsumptions={expandedPeriodConsumptions[periodID] || []}
                                isLoading={loadingExpandedPeriod === periodID}
                                canEdit={canEditPeriod}
                                onEditConsumption={(consumption) => onEditConsumption(consumption, period)}
                                onDeleteConsumption={(consumption) => onDeleteConsumption(consumption, period)}
                                onAdjustConsumption={(consumption) => onAdjustConsumption(consumption, period)}
                                onCalculate={async (e) => {
                                  if (e) e.stopPropagation()
                                  await onCalculateConsumptions(period, periodID)
                                }}
                                onSendToSAP={async (e) => {
                                  if (e) e.stopPropagation()
                                  await onSendToSAP(period)
                                }}
                                onDeletePeriod={null}
                                loadingConsumptions={loadingConsumptions}
                                sendingToSAP={sendingToSAP}
                                readOnly={readOnly}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

export default React.memo(ConsumptionPeriodsManager)
